import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, X, UserCheck, CheckSquare, Tags, GitMerge, UserCog } from "lucide-react";

type Ticket = {
  id: string; number: number; subject: string; status: string; priority: string; channel: string;
  category?: string | null; departmentId?: string | null; departmentName?: string | null;
  customerName?: string | null; customerEmail?: string | null; requesterUserId?: string | null;
  requesterName?: string | null; requesterEmail?: string | null;
  assignedAgentId?: string | null; assignedAgentName?: string | null;
  dueAt?: string | null; createdAt: string;
};
type ListResp = { items: Ticket[]; total: number; counts: Record<string, number> };
type Dept = { id: string; name: string };
type UserLite = { id: string; name: string; email?: string | null };

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "open", label: "Open" },
  { key: "pending", label: "Pending" },
  { key: "on_hold", label: "On hold" },
  { key: "solved", label: "Solved" },
  { key: "closed", label: "Closed" },
];

const VALID_STATUSES = ["open", "pending", "on_hold", "solved", "closed"];

const PRIORITY_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  urgent: "destructive", high: "default", normal: "secondary", low: "outline",
};

function statusLabel(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function dueState(dueAt?: string | null, status?: string) {
  if (!dueAt || status === "solved" || status === "closed") return null;
  const due = new Date(dueAt).getTime();
  const now = Date.now();
  if (due < now) return { label: "Overdue", cls: "text-red-600" };
  if (due - now < 4 * 3600 * 1000) return { label: "Due soon", cls: "text-amber-600" };
  return { label: new Date(dueAt).toLocaleDateString(), cls: "text-muted-foreground" };
}

export default function TicketsList() {
  const { user } = useAuth();

  // ── Filter state ───────────────────────────────────────────────────────────
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [channel, setChannel] = useState("");
  const [q, setQ] = useState("");
  const [myTickets, setMyTickets] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // ── New-ticket form ────────────────────────────────────────────────────────
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [newPriority, setNewPriority] = useState("normal");
  const [newDept, setNewDept] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [newDueAt, setNewDueAt] = useState("");
  const [newRequesterUserId, setNewRequesterUserId] = useState("");

  // ── Bulk selection state ───────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkAgentId, setBulkAgentId] = useState("");
  const [bulkTag, setBulkTag] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [bulkFeedback, setBulkFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // ── Queries ────────────────────────────────────────────────────────────────
  const depts = useQuery<Dept[]>({ queryKey: ["/api/departments/list"] });
  const requesters = useQuery<UserLite[]>({
    queryKey: ["/api/tickets/requesters"],
    queryFn: async () => (await apiRequest("GET", "/api/tickets/requesters")).json(),
    enabled: showCreate,
  });
  const agents = useQuery<UserLite[]>({
    queryKey: ["/api/tickets/agents"],
    queryFn: async () => (await apiRequest("GET", "/api/tickets/agents")).json(),
  });

  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (priority) params.set("priority", priority);
  if (departmentId) params.set("departmentId", departmentId);
  if (channel) params.set("channel", channel);
  if (q.trim()) params.set("q", q.trim());
  if (myTickets && user?.id) params.set("agentId", user.id);
  const qs = params.toString();

  const list = useQuery<ListResp>({
    queryKey: ["/api/tickets", qs],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/tickets${qs ? `?${qs}` : ""}`);
      return res.json();
    },
  });

  // ── Mutations ──────────────────────────────────────────────────────────────
  const create = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/tickets", {
        subject, description: description || null, priority: newPriority, channel: "manual",
        departmentId: newDept || null, customerName: customerName || null, customerEmail: customerEmail || null,
        requesterUserId: newRequesterUserId || null,
        dueAt: newDueAt ? new Date(newDueAt).toISOString() : null,
      });
      return res.json();
    },
    onSuccess: () => {
      setSubject(""); setDescription(""); setNewPriority("normal"); setNewDept("");
      setCustomerName(""); setCustomerEmail(""); setNewDueAt(""); setNewRequesterUserId("");
      setShowCreate(false);
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async (payload: object) => {
      const res = await apiRequest("POST", "/api/tickets/bulk", payload);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Bulk action failed");
      }
      return res.json() as Promise<{ updated: number; skipped: number }>;
    },
    onSuccess: (data) => {
      setBulkFeedback({ ok: true, msg: `Done: ${data.updated} updated, ${data.skipped} unchanged.` });
      setSelectedIds(new Set());
      setBulkStatus(""); setBulkAgentId(""); setBulkTag(""); setMergeTargetId("");
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
      setTimeout(() => setBulkFeedback(null), 4000);
    },
    onError: (err: Error) => {
      setBulkFeedback({ ok: false, msg: err.message });
      setTimeout(() => setBulkFeedback(null), 6000);
    },
  });

  // ── Selection helpers ──────────────────────────────────────────────────────
  const items = list.data?.items || [];
  const allSelected = items.length > 0 && items.every((t) => selectedIds.has(t.id));
  const someSelected = !allSelected && items.some((t) => selectedIds.has(t.id));

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) =>
      allSelected ? new Set() : new Set(items.map((t) => t.id))
    );
  }, [allSelected, items]);

  // ── Bulk action handlers ───────────────────────────────────────────────────
  function bulkChangeStatus(newStatus: string) {
    if (!newStatus || selectedIds.size === 0) return;
    bulkMutation.mutate({ action: "status", ticketIds: [...selectedIds], status: newStatus });
  }

  function bulkAssign(agentId: string) {
    if (selectedIds.size === 0) return;
    if (agentId === "__unassign__") {
      bulkMutation.mutate({ action: "assign", ticketIds: [...selectedIds], unassignAgent: true });
    } else {
      bulkMutation.mutate({ action: "assign", ticketIds: [...selectedIds], assignedAgentId: agentId });
    }
  }

  function bulkAddTag() {
    if (!bulkTag.trim() || selectedIds.size === 0) return;
    const tags = bulkTag.split(",").map((t) => t.trim()).filter(Boolean);
    bulkMutation.mutate({ action: "tag", ticketIds: [...selectedIds], tags });
    setBulkTag("");
  }

  function bulkMerge() {
    if (!mergeTargetId || selectedIds.size < 2) return;
    bulkMutation.mutate({ action: "merge", ticketIds: [...selectedIds], mergeIntoTicketId: mergeTargetId });
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const counts = list.data?.counts || {};
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0);
  const hasFilters = !!(priority || departmentId || channel || q.trim() || myTickets);

  function clearFilters() {
    setPriority(""); setDepartmentId(""); setChannel(""); setQ(""); setMyTickets(false);
  }

  // Selected tickets as objects (for merge target picker)
  const selectedTickets = items.filter((t) => selectedIds.has(t.id));

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="page-tickets-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tickets</h1>
          <p className="text-sm text-muted-foreground" data-testid="text-ticket-total">{list.data?.total ?? 0} ticket(s)</p>
        </div>
        <Button onClick={() => setShowCreate((v) => !v)} data-testid="button-toggle-create">
          {showCreate ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {showCreate ? "Cancel" : "New ticket"}
        </Button>
      </div>

      {showCreate && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" aria-label="Ticket subject" data-testid="input-ticket-subject" />
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" rows={3} aria-label="Ticket description" data-testid="input-ticket-new-description" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Select value={newPriority} onValueChange={setNewPriority}>
                <SelectTrigger aria-label="Ticket priority" data-testid="select-new-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newDept || "none"} onValueChange={(v) => setNewDept(v === "none" ? "" : v)}>
                <SelectTrigger aria-label="Ticket department" data-testid="select-new-department"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No department</SelectItem>
                  {(depts.data || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name (optional)" data-testid="input-customer-name" />
              <Input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="Customer email (optional)" data-testid="input-customer-email" />
              <div>
                <label htmlFor="ticket-new-due-at" className="text-xs text-muted-foreground mb-1 block">Due date (optional)</label>
                <Input id="ticket-new-due-at" type="date" value={newDueAt} onChange={(e) => setNewDueAt(e.target.value)} data-testid="input-new-due-at" />
              </div>
              <div>
                <span id="ticket-new-requester-label" className="text-xs text-muted-foreground mb-1 block">Requester — org user (optional)</span>
                <Select value={newRequesterUserId || "none"} onValueChange={(v) => setNewRequesterUserId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="select-new-requester" aria-labelledby="ticket-new-requester-label"><SelectValue placeholder="Select requester…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {(requesters.data || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}{u.email ? ` (${u.email})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => create.mutate()} disabled={!subject.trim() || create.isPending} data-testid="button-create-ticket">
                {create.isPending ? "Creating…" : "Create ticket"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status tabs with counts */}
      <div className="flex flex-wrap gap-1.5" data-testid="tabs-status">
        {STATUS_TABS.map((tab) => {
          const c = tab.key ? (counts[tab.key] || 0) : totalAll;
          const active = status === tab.key && !myTickets;
          return (
            <button
              key={tab.key || "all"}
              onClick={() => { setStatus(tab.key); setMyTickets(false); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
              data-testid={`tab-status-${tab.key || "all"}`}
            >
              {tab.label} <span className="ml-1 opacity-70">{c}</span>
            </button>
          );
        })}
        {user?.id && (
          <button
            onClick={() => { setMyTickets((v) => !v); setStatus(""); }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors inline-flex items-center gap-1 ${myTickets ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
            data-testid="tab-my-tickets"
          >
            <UserCheck className="w-3 h-3" />My tickets
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, customer…" className="pl-8" aria-label="Search tickets" data-testid="input-search-tickets" />
        </div>
        <Select value={priority || "all"} onValueChange={(v) => setPriority(v === "all" ? "" : v)}>
          <SelectTrigger className="sm:w-36" aria-label="Filter by priority" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any priority</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={departmentId || "all"} onValueChange={(v) => setDepartmentId(v === "all" ? "" : v)}>
          <SelectTrigger className="sm:w-44" aria-label="Filter by department" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {(depts.data || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={channel || "all"} onValueChange={(v) => setChannel(v === "all" ? "" : v)}>
          <SelectTrigger className="sm:w-36" aria-label="Filter by channel" data-testid="filter-channel"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any channel</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="chat">Live chat</SelectItem>
            <SelectItem value="call">Call</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-filters">
            <X className="w-4 h-4 mr-1" />Clear
          </Button>
        )}
      </div>

      {/* ── Bulk action toolbar (appears when ≥1 tickets selected) ─────────── */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-md" data-testid="bulk-toolbar">
          <span className="text-sm font-medium mr-1" data-testid="bulk-selected-count">
            {selectedIds.size} selected
          </span>

          {/* Change Status */}
          <Select
            value="__none__"
            onValueChange={(v) => { if (v !== "__none__") bulkChangeStatus(v); }}
          >
            <SelectTrigger className="h-8 w-40 text-xs" aria-label="Bulk change status" data-testid="bulk-select-status">
              <CheckSquare className="w-3.5 h-3.5 mr-1.5 shrink-0" />
              <SelectValue placeholder="Change status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" disabled>Change status…</SelectItem>
              {VALID_STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`bulk-status-${s}`}>{statusLabel(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Assign To */}
          <Select
            value="__none__"
            onValueChange={(v) => { if (v !== "__none__") bulkAssign(v); }}
          >
            <SelectTrigger className="h-8 w-40 text-xs" aria-label="Bulk assign to agent" data-testid="bulk-select-assign">
              <UserCog className="w-3.5 h-3.5 mr-1.5 shrink-0" />
              <SelectValue placeholder="Assign to…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" disabled>Assign to…</SelectItem>
              <SelectItem value="__unassign__">Unassign</SelectItem>
              {(agents.data || []).map((a) => (
                <SelectItem key={a.id} value={a.id} data-testid={`bulk-agent-${a.id}`}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Add Tag */}
          <div className="flex items-center gap-1">
            <div className="relative">
              <Tags className="absolute left-2 top-1.5 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={bulkTag}
                onChange={(e) => setBulkTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && bulkAddTag()}
                placeholder="Add tag(s)"
                className="h-8 pl-7 text-xs w-32"
                data-testid="bulk-input-tag"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs px-2"
              onClick={bulkAddTag}
              disabled={!bulkTag.trim() || bulkMutation.isPending}
              data-testid="bulk-button-add-tag"
            >
              Add
            </Button>
          </div>

          {/* Merge — only when 2+ tickets selected */}
          {selectedIds.size >= 2 && (
            <div className="flex items-center gap-1">
              <Select value={mergeTargetId || "__none__"} onValueChange={(v) => setMergeTargetId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-8 w-44 text-xs" aria-label="Merge into ticket" data-testid="bulk-select-merge-target">
                  <GitMerge className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                  <SelectValue placeholder="Merge into…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>Merge into…</SelectItem>
                  {selectedTickets.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`bulk-merge-target-${t.id}`}>
                      #{t.number} — {t.subject.slice(0, 30)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs px-2"
                onClick={bulkMerge}
                disabled={!mergeTargetId || selectedIds.size < 2 || bulkMutation.isPending}
                data-testid="bulk-button-merge"
              >
                Merge
              </Button>
            </div>
          )}

          {bulkMutation.isPending && (
            <span className="text-xs text-muted-foreground">Applying…</span>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs ml-auto"
            onClick={() => { setSelectedIds(new Set()); setBulkStatus(""); setBulkAgentId(""); setBulkTag(""); setMergeTargetId(""); }}
            data-testid="bulk-button-deselect"
          >
            <X className="w-3 h-3 mr-1" />Deselect all
          </Button>
        </div>
      )}

      {/* Bulk feedback toast */}
      {bulkFeedback && (
        <div
          className={`text-sm px-4 py-2 rounded-md border ${bulkFeedback.ok ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}
          role="status"
          data-testid="bulk-feedback"
        >
          {bulkFeedback.msg}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  {/* Select-all checkbox */}
                  <th className="py-2.5 px-3 w-10">
                    <Checkbox
                      checked={allSelected}
                      data-state={someSelected ? "indeterminate" : allSelected ? "checked" : "unchecked"}
                      onCheckedChange={toggleAll}
                      aria-label="Select all tickets"
                      data-testid="checkbox-select-all"
                    />
                  </th>
                  <th className="py-2.5 px-3 w-16">#</th>
                  <th className="py-2.5 px-3">Subject</th>
                  <th className="py-2.5 px-3 hidden sm:table-cell">Requester</th>
                  <th className="py-2.5 px-3 hidden lg:table-cell">Department</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3 hidden md:table-cell">Priority</th>
                  <th className="py-2.5 px-3 hidden lg:table-cell">Assigned to</th>
                  <th className="py-2.5 px-3 hidden lg:table-cell">Due</th>
                  <th className="py-2.5 px-3 hidden md:table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading && (
                  <tr><td colSpan={10} className="text-center py-8 text-muted-foreground text-xs">Loading…</td></tr>
                )}
                {!list.isLoading && items.map((t) => {
                  const due = dueState(t.dueAt, t.status);
                  const isSelected = selectedIds.has(t.id);
                  return (
                    <tr
                      key={t.id}
                      className={`border-b hover:bg-muted/30 ${isSelected ? "bg-primary/5" : ""}`}
                      data-testid={`row-ticket-${t.id}`}
                    >
                      <td className="py-2.5 px-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleOne(t.id)}
                          aria-label={`Select ticket #${t.number}`}
                          data-testid={`checkbox-ticket-${t.id}`}
                        />
                      </td>
                      <td className="py-2.5 px-3 text-muted-foreground tabular-nums" data-testid={`text-ticket-number-${t.id}`}>#{t.number}</td>
                      <td className="py-2.5 px-3">
                        <Link href={`/tickets/${t.id}`} className="font-medium hover:underline" data-testid={`link-ticket-${t.id}`}>{t.subject}</Link>
                        {t.category && <span className="ml-2 text-xs text-muted-foreground">{t.category}</span>}
                      </td>
                      <td className="py-2.5 px-3 hidden sm:table-cell text-muted-foreground">
                        {t.requesterName || t.requesterEmail || t.customerName || t.customerEmail || "—"}
                      </td>
                      <td className="py-2.5 px-3 hidden lg:table-cell">{t.departmentName ? <Badge variant="outline">{t.departmentName}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="py-2.5 px-3"><Badge variant="outline">{statusLabel(t.status)}</Badge></td>
                      <td className="py-2.5 px-3 hidden md:table-cell"><Badge variant={PRIORITY_VARIANT[t.priority] || "secondary"}>{t.priority}</Badge></td>
                      <td className="py-2.5 px-3 hidden lg:table-cell text-xs text-muted-foreground">{t.assignedAgentName || <span className="italic">Unassigned</span>}</td>
                      <td className={`py-2.5 px-3 hidden lg:table-cell text-xs ${due?.cls || "text-muted-foreground"}`} data-testid={`text-due-${t.id}`}>{due?.label || "—"}</td>
                      <td className="py-2.5 px-3 hidden md:table-cell text-xs text-muted-foreground">{new Date(t.createdAt).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
                {!list.isLoading && items.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-10 text-muted-foreground text-xs" data-testid="text-no-tickets">No tickets match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
